import React, { useCallback, useEffect, useState } from "react";

import { getAllFields, getFieldShapeAdmin, getSoilFieldDelineation } from "services";

import { useCancelAsyncOnUnmount } from "utils";

import { AdminHead, Col, Form, MainPage, Option, Row, Select, Spin, TagNormal } from "components";

import { CropShape } from "pages/admin/experiments/components/CropShape";

export const Delineation = () => {
  const [fields, setFields] = useState([]);
  const [location, setLocation] = useState(false);
  const [currentMap, setCurrentMap] = useState(null);
  const [savedGeometry, setSavedGeometry] = useState(false);
  const [newGeometry, setNewGeometry] = useState(false);
  const [field, setField] = useState({ size: 0 });
  const [page, setPage] = useState(0);

  const signal = useCancelAsyncOnUnmount()();

  const loadAllFields = useCallback(() => {
    getAllFields({
      page,
      signal,
    }).then((res) => {
      setFields((fields) => [...fields, ...res.fields]);
      if (res.count > page * 250 && res.count > 250) {
        setPage(page + 1);
      }
    });
  }, [page, signal]);

  const updateField = (v) => {
    const field = fields[v];
    setCurrentMap(null);

    setSavedGeometry(false);
    setNewGeometry(false);
    setField(field);
    setLocation(field.location);

    getFieldShapeAdmin({
      fieldId: field.id,
      signal,
    }).then((res) => {
      if (res.fieldShape) {
        setSavedGeometry(res.fieldShape);
      }
    });
  };

  const loadDelineation = () => {
    getSoilFieldDelineation({
      bbox: currentMap.bbox,
      size: currentMap.size,
      location,
      signal,
    }).then((res) => {
      if (res.geometry.length) {
        setNewGeometry({
          fieldId: savedGeometry.fieldId,
          geometry: res.geometry,
          center: savedGeometry.center,
        });
      }
    });
  };

  const getMapData = (data) => {
    // get size and bounding box from map change handler
    const { size } = data;
    const bbox = [data.marginBounds.sw.lng, data.marginBounds.sw.lat, data.marginBounds.ne.lng, data.marginBounds.ne.lat];

    setCurrentMap({ bbox, size });
  };

  useEffect(() => {
    if (currentMap && location && currentMap.bbox[0] !== 13.378683018957531 && currentMap.bbox[1] !== 52.51726052076745) {
      loadDelineation();
    }
  }, [currentMap]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadAllFields();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <MainPage pageName={"admin"}>
      <AdminHead
        headline={
          <>
            Field delineation <TagNormal text="ALPHA" style={{ transform: "translate(0, -50%)", fontWeight: "bold" }} />
          </>
        }
        description={
          <>
            Choose a field to map below (only the first 250 fields are retrieved for this experiment) - nothing will be shown if the field geometry is not
            available. You can then compare our field delineation method to the saved shape. It's recommended to use fields larger than several hectares - less
            than that is likely a tree or something.
          </>
        }
      />
      <div className="content">
        <Row>
          <Col sm={24} md={12}>
            <Form>
              <Select
                name="field"
                label="Select field"
                rules={[{ required: true }]}
                style={{ width: "100%" }}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) => option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                onChange={(v) => updateField(v)}
                optionLabelProp={"label"}
                disabled={!newGeometry && savedGeometry}
              >
                {fields.map((field, i) => {
                  if (field.location && field.size >= 2) {
                    return (
                      <Option value={i} key={i} label={field.name}>
                        {field.name}
                        <br />
                        <small>{field.size.toFixed(2)}ha</small>
                      </Option>
                    );
                  }
                  return null;
                })}
              </Select>
            </Form>
          </Col>
        </Row>
        <Row>
          <Col sm={24} md={12}>
            <CropShape field={field} geometry={savedGeometry} location={location} setBbox={getMapData} />
            {savedGeometry && <h3 className="center">Saved shape</h3>}
          </Col>
          {newGeometry ? (
            <Col sm={24} md={12}>
              <CropShape field={field} geometry={newGeometry} location={location} />
              <h3 className="center">New shape</h3>
            </Col>
          ) : savedGeometry ? (
            <Col sm={24} md={12} className="center">
              <Spin />
            </Col>
          ) : (
            <></>
          )}
        </Row>
      </div>
    </MainPage>
  );
};
